/**
 * What a bitrate number MEANS, given what is being captured and what is encoding it.
 *
 * The congestion cuts used to be fixed numbers: a soft cut at a fraction of the profile
 * ceiling and a hard cut at a flat RECOVERY_MAX_BITRATE_BPS. A fixed number cannot be right,
 * because the same bitrate is a different picture at every capture resolution and under every
 * codec. Measured against the live ShadowCam profiles, the flat 1.5 Mbps hard cut was wrong in
 * BOTH directions at once:
 *
 *   ShadowCam00  640x360@60   13.8 Mpx/s   1.5 Mbps = 0.109 bpp   ABOVE a clean picture
 *   ShadowCam01  896x480@60   25.8 Mpx/s   1.5 Mbps = 0.058 bpp   marginal
 *   ShadowCam03 1280x720@60   55.3 Mpx/s   1.5 Mbps = 0.027 bpp   unusable
 *
 * So the emergency cut barely cut anything on the tier a struggling mobile guest gets demoted
 * to, and destroyed the picture on the tier desktop is permanently pinned to. Both follow from
 * the number being blind to the two things that decide what it buys.
 *
 * This module expresses the cuts in bits per pixel instead, then converts to bps against the
 * live capture and the negotiated codec. Absent context reproduces the old constants exactly,
 * so a caller that cannot say what it is encoding is no worse off than before.
 */

import {
  DESKTOP_MIN_BITRATE_BPS,
  RECOVERY_ELEVATED_BITRATE_FRACTION,
  RECOVERY_MAX_BITRATE_BPS,
  TARGET_FRAMERATE,
  type StreamResolution,
} from './webrtcLatencyContracts';
import {
  videoQualityPolicy,
  type VideoCaptureMode,
  type VideoPlatform,
} from './mediaPolicy/VideoQualityPolicy';
import type { HardwareAccelerationKind } from './webrtcLatencyContracts';

/** Video codec families the send path can land on. Mirrors the hardware codec ladder. */
export type VideoCodecFamily = 'h264' | 'h265';

/**
 * Bitrate needed for equal perceived quality, relative to H.264.
 *
 * DELIBERATELY CONSERVATIVE — closer to 1.0 than published BD-rate savings suggest. Those
 * figures come from slow offline encodes with full lookahead; a real-time hardware encoder
 * running at low latency, with no frame reordering and small GOPs, realizes appreciably less
 * of the gain. Erring high costs some bandwidth. Erring low starves the encoder, which is the
 * exact failure this module exists to fix, so the asymmetry is intentional.
 *
 * An unknown codec is treated as H.264 — the least efficient thing we would realistically be
 * sending, and therefore the safe assumption.
 */
export const CODEC_BITRATE_MULTIPLIER: Record<VideoCodecFamily, number> = {
  h264: 1,
  h265: 0.75,
};

/**
 * Bits per pixel for 60fps game content, which is the hard case: high motion, sharp UI edges,
 * and no opportunity to spend bits on a slow pan.
 *
 * Calibrated against well-known operating points rather than invented. Twitch's 720p60 ceiling
 * of ~4.5 Mbps is 0.081 bpp and is generally judged decent; its 1080p60 ceiling of 6 Mbps is
 * 0.048 bpp and is visibly blocky on motion. `floor` therefore sits at the bottom of what still
 * resolves motion, not at the bottom of what still decodes.
 *
 * ESTIMATES. They are honest reference-anchored ones, but nobody has run a formal quality sweep
 * on this content, and they should be revisited against real captures before being treated as
 * settled.
 */
export const GAMING_BITS_PER_PIXEL = {
  /** A stream nobody would describe as compressed. The steady-state target. */
  clean: 0.1,
  /** Visibly leaner, still coherent on motion. The elevated (soft) cut. */
  reduced: 0.07,
  /** The least that still resolves motion and UI. The critical (hard) cut. */
  floor: 0.05,
} as const;

/**
 * How far above `clean` a recovered stream is allowed to climb.
 *
 * Ramping a 360p capture back to an 8 Mbps profile ceiling spends four times what the picture
 * can use, on a link that just proved it was struggling. Headroom above clean is worth having —
 * a scene change or a burst of motion should not immediately re-cut — but unbounded headroom is
 * how a recovered stream re-congests the link it just recovered from.
 */
export const QUALITY_HEADROOM_FACTOR = 1.5;

/** Everything needed to turn a bits-per-pixel target into a bitrate. */
export type StreamEncodeContext = {
  /** The resolution actually being captured and encoded — not the per-peer tier hint. */
  resolution: StreamResolution;
  /** Defaults to TARGET_FRAMERATE. */
  frameRate?: number;
  /** Absent → treated as h264, the conservative assumption. */
  codec?: VideoCodecFamily;
  /** Browser/device profile controlling cadence and quality trade-offs. */
  platform?: VideoPlatform;
  /** Measured encoder implementation; unknown remains distinct from software. */
  encoder?: HardwareAccelerationKind;
  /** Native capture source used to size scene-change headroom. */
  captureMode?: VideoCaptureMode;
};

/** Platform/codec/encoder/capture-specific headroom, with the legacy factor as the unknown fallback. */
export function qualityHeadroomFactorFor(context?: StreamEncodeContext): number {
  if (!context || (!context.platform && !context.encoder && !context.captureMode)) return QUALITY_HEADROOM_FACTOR;
  return videoQualityPolicy.headroomFactorFor({
    platform: context.platform ?? 'unknown',
    codec: context.codec === 'h265' ? 'hevc' : context.codec ?? 'unknown',
    encoder: context.encoder ?? 'unknown',
    captureMode: context.captureMode ?? 'unknown',
  });
}

/** Map an SDP mimeType to an active hardware codec family. */
export function videoCodecFamilyOf(mimeType?: string): VideoCodecFamily | undefined {
  if (!mimeType) return undefined;
  const subtype = mimeType.toLowerCase().split('/').pop() ?? '';
  if (subtype === 'h264') return 'h264';
  if (subtype === 'h265' || subtype === 'hevc') return 'h265';
  return undefined;
}

/** Pixels encoded per second — the quantity a bitrate is actually spent on. */
export function pixelRateFor(resolution: StreamResolution, frameRate: number): number {
  const { width, height } = resolution;
  if (!Number.isFinite(width) || !Number.isFinite(height) || !Number.isFinite(frameRate)) return 0;
  if (width <= 0 || height <= 0 || frameRate <= 0) return 0;
  return width * height * frameRate;
}

/**
 * Bitrate (bps) that delivers `bitsPerPixel` for this capture under this codec.
 *
 * Returns 0 for an unusable context so callers can fall back rather than write a nonsense
 * ceiling onto a sender.
 */
export function qualityBitrateBps(context: StreamEncodeContext, bitsPerPixel: number): number {
  const pixelRate = pixelRateFor(context.resolution, context.frameRate ?? TARGET_FRAMERATE);
  if (pixelRate <= 0 || !Number.isFinite(bitsPerPixel) || bitsPerPixel <= 0) return 0;
  const multiplier = CODEC_BITRATE_MULTIPLIER[context.codec ?? 'h264'];
  return Math.max(1, Math.round(pixelRate * bitsPerPixel * multiplier));
}

/**
 * Elevated (soft) cut ceiling.
 *
 * With context: the bitrate that still renders `reduced` quality for what is being captured.
 * Without it: the old fraction of the profile ceiling, unchanged.
 *
 * Never above the profile ceiling — an operator cap is a cap — and never above the old
 * fractional cut either, so a soft cut always remains a real reduction. That second clamp is
 * what stops a small capture from turning "cut the bitrate" into "leave it alone": at 360p the
 * quality target sits well under the ceiling anyway, but on a low operator cap it could
 * otherwise land at the cap and mitigate nothing.
 */
export function softCutBitrateBps(
  profileMaxBitrateBps: number,
  context?: StreamEncodeContext,
): number {
  const fractional = Math.max(
    1,
    Math.round(profileMaxBitrateBps * RECOVERY_ELEVATED_BITRATE_FRACTION),
  );
  if (!Number.isFinite(profileMaxBitrateBps) || profileMaxBitrateBps <= 0) return 1;
  if (!context) return fractional;

  const quality = qualityBitrateBps(context, GAMING_BITS_PER_PIXEL.reduced);
  if (quality <= 0) return fractional;
  return Math.max(1, Math.min(quality, fractional, profileMaxBitrateBps));
}

/**
 * Critical (hard) cut ceiling.
 *
 * With context: the quality floor for this capture and codec — the point past which cutting
 * further stops relieving congestion in any useful sense, because the picture it protects is
 * already gone. Congestion that persists here is not answerable by bitrate; it needs a smaller
 * native capture, which is a different lever and deliberately not this module's business.
 *
 * Without context: the old flat RECOVERY_MAX_BITRATE_BPS behaviour.
 *
 * Always at or below the soft cut, so a soft→hard escalation can never raise the ceiling.
 */
export function hardCutBitrateBpsFor(
  profileMaxBitrateBps: number,
  context?: StreamEncodeContext,
): number {
  const soft = softCutBitrateBps(profileMaxBitrateBps, context);
  if (!context) return Math.min(RECOVERY_MAX_BITRATE_BPS, soft);

  const quality = qualityBitrateBps(context, GAMING_BITS_PER_PIXEL.floor);
  if (quality <= 0) return Math.min(RECOVERY_MAX_BITRATE_BPS, soft);
  return Math.max(1, Math.min(quality, soft));
}

/** Min and max bitrate for one capture. */
export type SteadySendBand = {
  minBitrateBps: number;
  maxBitrateBps: number;
};

/**
 * The ceiling this capture should actually be given, with the existing settle floor.
 *
 * The profile ceiling (6–10 Mbps) sits far above a 480p picture. The encoder banks the
 * unused budget and releases it as one fat frame, which is the hitch. The max is the
 * picture climb ceiling.
 *
 * The min stays the settle floor. It is written into the SDP once and held for the
 * session, and only H.264 reads it. Raising it to the clean rate of a larger picture
 * fights a later 360p cut and, on a link slower than that rate, holds a standing queue.
 */
export function steadySendBandBps(
  profileMaxBitrateBps: number,
  context?: StreamEncodeContext,
): SteadySendBand {
  const maxBitrateBps = recoveryClimbCeilingBps(profileMaxBitrateBps, context);
  const minBitrateBps = Math.max(1, Math.min(DESKTOP_MIN_BITRATE_BPS, maxBitrateBps));
  return { minBitrateBps, maxBitrateBps };
}

/**
 * Ceiling the post-recovery climb ramps toward.
 *
 * Without context this is the profile ceiling, exactly as before. With it, the climb stops at
 * clean quality plus headroom — spending more than the picture can use, on a link that was
 * congested moments ago, is how a recovered stream re-congests itself.
 */
export function recoveryClimbCeilingBps(
  profileMaxBitrateBps: number,
  context?: StreamEncodeContext,
): number {
  if (!Number.isFinite(profileMaxBitrateBps) || profileMaxBitrateBps <= 0) return 1;
  if (!context) return profileMaxBitrateBps;

  const clean = qualityBitrateBps(context, GAMING_BITS_PER_PIXEL.clean);
  if (clean <= 0) return profileMaxBitrateBps;
  return Math.max(1, Math.min(profileMaxBitrateBps, Math.round(clean * qualityHeadroomFactorFor(context))));
}
